import React, { useEffect } from "react"
import { MobergTheme } from "../../../../../../Moberg"
import { useCheckRecordingInfo } from "../../Hooks/useCheckRecordingInfo"
import { DurationUnit, useDuration, getFormattedDuration } from "../../Hooks/useDuration"
import ConfirmationModal from "../../../../../../Components/ConfirmationModal/ConfirmationModal"
import { useModalProvider } from "../../../../../../Providers/ModalProvider"
import { currentPatientFileInfoAtom } from "../../Atoms/PatientFile"
import { useRecoilValue } from "recoil"

type LiveReviewDelayWarningProps = {
    dataObjectId: number
    neededTimeMilliseconds: number
}

export function getNextArchiveTime(timestamp: number, archiveIntervalMilliseconds=120_000): number {
    return timestamp + (archiveIntervalMilliseconds - timestamp % archiveIntervalMilliseconds)
}


export const LiveReviewDelayWarning = ({ dataObjectId, neededTimeMilliseconds }: LiveReviewDelayWarningProps) => {
    const { close } = useModalProvider()
    const { isAdmitted, fileStartDate, fileEndDate } = useRecoilValue(currentPatientFileInfoAtom)
    const nextArchiveTime = getNextArchiveTime(fileStartDate.getTime() + neededTimeMilliseconds)
    const [duration, updateDuration] = useDuration({value: Math.round(Math.max(0, nextArchiveTime - new Date(Date.now()).getTime())/1000), units: DurationUnit.SECONDS})
    const minimumDurationMet = (fileEndDate.getTime() - fileStartDate.getTime() > neededTimeMilliseconds)

    // Update the recording info as it becomes available
    useCheckRecordingInfo({ dataObjectId, intervalMilliseconds: 10_000 })

    useEffect(() => {
        if (!isAdmitted) {
            return
        }

        const intervalId = setInterval(() => {
            const remainingSeconds = Math.round(Math.max(0, nextArchiveTime - new Date(Date.now()).getTime())/1000)
            updateDuration(remainingSeconds, DurationUnit.SECONDS)
            
            if (minimumDurationMet) {
                clearTimeout(intervalId)
                close()
            }
        }, 1000)

        return () => {
            clearTimeout(intervalId)
        }
    }, [nextArchiveTime, isAdmitted, updateDuration, neededTimeMilliseconds, close, fileEndDate, minimumDurationMet])

    const getDescription = () => {
        if (fileStartDate.getTime() === 0) {
            return "No data has been stored to the archive. Either no data has been collected, or the data has not been stored to the filesystem. Check again in 2 minutes."
        } else if (duration.milliseconds > 0) {
            return `Data is is currently unavailable because it hasn't been stored to the filesystem. It will be available in approximately ${getFormattedDuration(duration)} (${new Date(nextArchiveTime)})`
        } else {
            return "Data will be available momentarily. Please wait..."
        }
    }

    return (
        <ConfirmationModal
            title={"Waiting for archive..."}
            description={getDescription()}
            confirmButton={{ text: "OK", theme: MobergTheme.BLUE, onClick: close }}
            showCancelButton={false}
        />
    )
}
